import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface HeroBannerProps {
  topArticles: Article[];
}

export default function HeroBanner({ topArticles }: HeroBannerProps) {
  const [current, setCurrent] = useState(0);
  const total = topArticles.length;

  const goTo = useCallback((index: number) => {
    setCurrent((index + total) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 5000);
    return () => clearInterval(timer);
  }, [total]);

  if (total === 0) return null;

  const article = topArticles[current];

  return (
    <section className="relative w-full overflow-hidden" aria-label="头条轮播">
      <div className="relative aspect-[21/9] md:aspect-[21/8] min-h-[360px] md:min-h-[480px]">
        {topArticles.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === current ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <img
              src={item.imageUrl}
              alt={item.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/20 via-background-950/30 to-background-950 z-20"></div>

        <div className="absolute bottom-0 left-0 right-0 z-30 p-4 md:p-8 lg:p-12">
          <Link to={`/article/${article.id}`} className="block cursor-pointer">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="text-xs font-bold text-background-950 bg-primary-500 px-3 py-1 rounded-full whitespace-nowrap">
                头条
              </span>
              {article.tags.slice(0, 2).map((tag) => (
                <span key={tag} className="text-xs text-primary-300 bg-primary-500/15 px-3 py-1 rounded-full whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-heading font-black text-foreground-50 leading-tight max-w-3xl">
              {article.title}
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-300 max-w-2xl line-clamp-2">
              {article.excerpt}
            </p>
          </Link>

          <div className="mt-4 md:mt-6 flex items-center gap-2">
            {topArticles.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  index === current
                    ? "w-8 bg-primary-500"
                    : "w-4 bg-foreground-600 hover:bg-foreground-400"
                }`}
                aria-label={`切换到第${index + 1}条`}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}